import React, { useState, useEffect, useMemo } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Btn, Sel, Input } from "./ui";
import { Icon } from "./Icons";
import { supabase, isSupabaseReady } from "./supabase";
import { projetoTexto } from "./PlantaoModais";

const STATUS = {
  rascunho: { label: "Rascunho", cor: T.textDim },
  enviada:  { label: "Enviada",  cor: T.info },
  aceita:   { label: "Aceita",   cor: T.ok },
  recusada: { label: "Recusada", cor: T.err },
};

// ═══════════════════════════════════════════════════════════════════════
// PROPOSTAS (admin) — subitem de Vendas. Ponte pro Wix Proposals:
// o corretor cria a proposta no app → aqui o gestor copia a ficha, cria no
// Wix Proposals com o template e envia. Cliente assina → venda + lead fechado.
// ═══════════════════════════════════════════════════════════════════════
export default function Propostas({ corretores = [], currentUser }) {
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStatus, setFStatus] = useState("todas");
  const [busca, setBusca] = useState("");
  const [copiado, setCopiado] = useState(null);

  const corMap = useMemo(() => {
    const m = {};
    corretores.forEach(c => { if (c._uuid) m[c._uuid] = c; if (c.id) m[c.id] = c; });
    return m;
  }, [corretores]);

  async function carregar() {
    if (!isSupabaseReady) { setLoading(false); return; }
    try {
      const { data } = await supabase.from("propostas").select("*").order("created_at", { ascending: false }).limit(500);
      setPropostas(data || []);
    } catch (e) { console.error("[Propostas]", e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  const filtradas = propostas.filter(p => {
    if (fStatus !== "todas" && p.status !== fStatus) return false;
    if (busca) { const q = busca.toLowerCase(); return (p.cliente_nome || "").toLowerCase().includes(q); }
    return true;
  });

  function copiar(p) {
    const lead = { pedido_info: p.pedido_info || {}, moveis: p.moveis || [], financeiro: p.financeiro || {}, nome_lead: p.cliente_nome };
    const txt = projetoTexto(lead) + (p.template ? `\n\nTemplate da proposta: ${p.template}` : "");
    try { navigator.clipboard.writeText(txt); } catch (_) {}
    setCopiado(p.id);
    setTimeout(() => setCopiado(null), 2000);
  }

  async function marcarAceita(p) {
    if (!window.confirm("Marcar proposta como ACEITA?\n\nO lead do plantão vira FECHADO. A venda entra automaticamente pelo pedido do Wix quando o cliente assinar/pagar.")) return;
    try {
      await supabase.from("propostas").update({ status: "aceita" }).eq("id", p.id);
      if (p.lead_id) await supabase.from("plantao_leads").update({ status: "fechado", fechado_em: new Date().toISOString(), valor_fechado: p.valor_total || null }).eq("id", p.lead_id);
      setPropostas(prev => prev.map(x => x.id === p.id ? { ...x, status: "aceita" } : x));
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function marcarRecusada(p) {
    if (!window.confirm("Marcar proposta como RECUSADA?")) return;
    try {
      await supabase.from("propostas").update({ status: "recusada" }).eq("id", p.id);
      setPropostas(prev => prev.map(x => x.id === p.id ? { ...x, status: "recusada" } : x));
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function excluir(p) {
    if (!window.confirm(`Excluir a proposta de ${p.cliente_nome || "?"}?`)) return;
    try {
      await supabase.from("propostas").delete().eq("id", p.id);
      setPropostas(prev => prev.filter(x => x.id !== p.id));
    } catch (e) { alert("Erro: " + e.message); }
  }

  const totalEnviadas = propostas.filter(p => p.status === "enviada").length;
  const totalAceitas = propostas.filter(p => p.status === "aceita").length;
  const volumeAceito = propostas.filter(p => p.status === "aceita").reduce((s, p) => s + (parseFloat(p.valor_total) || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionHeader icon="📄" title="Propostas" subtitle="Propostas criadas pelos corretores — copie a ficha e crie no Wix Proposals" />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <Kpi label="Total" valor={propostas.length} cor={T.brand} />
        <Kpi label="Enviadas" valor={totalEnviadas} cor={T.info} />
        <Kpi label="Aceitas" valor={totalAceitas} cor={T.ok} />
        <Kpi label="Volume aceito" valor={fmt.brl(volumeAceito)} cor={T.gold} isText />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Sel value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ minWidth: 150 }}>
          <option value="todas">📊 Todas</option>
          <option value="enviada">📤 Enviadas</option>
          <option value="aceita">✅ Aceitas</option>
          <option value="recusada">❌ Recusadas</option>
        </Sel>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Input placeholder="Buscar por cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Carregando propostas…</div>
      ) : filtradas.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13 }}>
          {propostas.length === 0 ? "Nenhuma proposta ainda. O corretor cria pelo Plantão → Criar proposta." : "Nenhuma proposta com esse filtro."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtradas.map(p => {
            const st = STATUS[p.status] || STATUS.rascunho;
            const cor = p.corretor_id ? corMap[p.corretor_id] : null;
            const nMoveis = Array.isArray(p.moveis) ? p.moveis.length : 0;
            const data = p.created_at ? new Date(p.created_at).toLocaleDateString("pt-BR") : "—";
            return (
              <Card key={p.id} style={{ borderLeft: `3px solid ${st.cor}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ padding: "2px 8px", borderRadius: 100, background: st.cor + "22", color: st.cor, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{st.label}</span>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{p.cliente_nome || "— sem nome —"}</div>
                    </div>
                    <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
                      {fmt.brl(parseFloat(p.valor_total) || 0)} · {nMoveis} móvel(is)
                      {p.template && ` · Template: ${p.template}`}
                      {cor && ` · 👤 ${cor.nome}`}
                      {` · ${data}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Btn variant="ghost" onClick={() => copiar(p)} style={{ fontSize: 11 }}>{copiado === p.id ? "✓ Copiado!" : "📋 Copiar ficha"}</Btn>
                    {p.status !== "aceita" && <Btn variant="success" onClick={() => marcarAceita(p)} style={{ fontSize: 11 }}>✅ Marcar aceita</Btn>}
                    {p.status !== "recusada" && p.status !== "aceita" && <Btn variant="ghost" onClick={() => marcarRecusada(p)} style={{ fontSize: 11, color: T.err }}>❌ Recusada</Btn>}
                    <button onClick={() => excluir(p)} style={{ background: "none", border: "none", cursor: "pointer", color: T.err, fontSize: 11, fontFamily: "inherit", textDecoration: "underline" }}>🗑️</button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, valor, cor, isText }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${cor}`, borderRadius: T.r.md, padding: "12px 10px" }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div className="serif" style={{ fontSize: isText ? 15 : 22, fontWeight: 700, color: T.text }}>{valor}</div>
    </div>
  );
}
